import type React from 'react';
import { useMemo, useState } from 'react';
import Button from '@components/Button';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useEntityStore } from '@ContractBuilder/store/entity.store';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationIcon, XIcon } from '@heroicons/react/outline';
import { useDeepCompareCallback } from '@hooks/use-deep-compare-callback';
import { patchEntity } from '@mutations/patchEntity';
import { ROOT_SECTIONS_KEY, SPLIT_BY_SECTIONS_KEY } from '@root/helpers/schema';
import clsx from 'clsx';
import { cloneDeep, get, isEmpty as isEmptyFn, set } from 'lodash-es';

import { SectionRepeaterView } from './SectionRepeater.view';
import { SectionRepeaterCollapsibleView } from './SectionRepeaterCollapsible.view';
import type { SectionRepeaterProps } from './types';

const CUSTOM_CONTENT: Record<string, string> = {
  sections: 'Adding sections will enable the option to split content into each section.',
};

const SectionRepeater: React.FC<SectionRepeaterProps> = ({
  autoGrow,
  className,
  id,
  item,
  ownKey,
  validationErrors,
  showQuestionKey,
  onChange,
  isReadOnly = false,
  isDisabled,
  collapsible = true,
  startCollapsed = false,
  hideErrors,
  isRequired,
}) => {
  const [isConfirmSectionModalOpen, setIsConfirmSectionModalOpen] = useState(false);
  const [pendingSectionIndex, setPendingSectionIndex] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<'ADD' | 'REMOVE' | null>(null);
  const [isSectionActionLoading, setIsSectionActionLoading] = useState(false);
  const { value, setFormValues } = useDatapointsStore(({ formValues, setFormValues }) => ({
    value: get(formValues, ownKey, item.value ?? ''),
    setFormValues,
  }));

  const hasInitialValue = value && Array.isArray(value) && value.length > 0;
  const defaultValue = [];
  for (let i = 0; i < (item?.minimum ?? 0); i++) {
    defaultValue.push({});
  }
  const initialItems = hasInitialValue ? value : defaultValue;
  const canAdd = item?.maximum === undefined || initialItems?.length < item?.maximum;
  const total100Error = validationErrors?.find(
    (error) => error.instancePath === ownKey && error.keyword === 'total100',
  );

  const schemaItem = item.items?.type === 'object' ? item.items : { properties: { [id]: item.items } };

  const handleAdd = useDeepCompareCallback(
    async (index: number) => {
      const formValues = useDatapointsStore.getState().formValues;
      const hasSplitBySections = get(formValues, 'risk_details.split_by_sections.length', 0) > 0;
      if (ownKey === ROOT_SECTIONS_KEY && hasSplitBySections) {
        setIsConfirmSectionModalOpen(true);
        setPendingSectionIndex(index);
        setPendingAction('ADD');
        return;
      }

      const cloned = cloneDeep(formValues);
      const newItems = [...initialItems.slice(0, index), {}, ...initialItems.slice(index)];
      set(cloned, ownKey, newItems);
      setFormValues(cloned);
    },
    [ownKey, initialItems, setFormValues],
  );

  const handleRemove = useDeepCompareCallback(
    (itemIndex: number) => {
      if (ownKey === ROOT_SECTIONS_KEY && !isConfirmSectionModalOpen) {
        setIsConfirmSectionModalOpen(true);
        setPendingSectionIndex(itemIndex);
        setPendingAction('REMOVE');
        return;
      }

      const formValues = useDatapointsStore.getState().formValues;
      const cloned = cloneDeep(formValues);
      const filtered = initialItems.filter((_v, index) => index !== itemIndex);
      if (ownKey === ROOT_SECTIONS_KEY && filtered.length === 0) {
        set(cloned, SPLIT_BY_SECTIONS_KEY, []);
      }
      set(cloned, ownKey, filtered);
      setFormValues(cloned);
    },
    [initialItems, isConfirmSectionModalOpen, ownKey, setFormValues],
  );

  const handleConfirmSectionAction = async () => {
    if (pendingSectionIndex === null) return;

    if (ownKey === ROOT_SECTIONS_KEY && pendingAction) {
      setIsSectionActionLoading(true);
      try {
        await patchEntity({
          operation: ApiPatchOperation.AddOrRemoveDataSections,
          data_items: useDatapointsStore.getState().formValues,
          sectionIndex: pendingSectionIndex,
          action: pendingAction,
        });
        await useEntityStore.getState().reloadSubmission();
        setIsConfirmSectionModalOpen(false);
        setPendingSectionIndex(null);
        setPendingAction(null);
      } finally {
        setIsSectionActionLoading(false);
      }
      return;
    }

    handleRemove(pendingSectionIndex);
    setIsConfirmSectionModalOpen(false);
    setPendingSectionIndex(null);
    setPendingAction(null);
  };

  const handleCancelSectionAction = () => {
    if (isSectionActionLoading) return;
    setIsConfirmSectionModalOpen(false);
  };

  const isEmpty = initialItems.length === 0;
  const emptyContent = isEmpty ? CUSTOM_CONTENT[id] : '';

  const helperText = item.helperText ?? item.description;

  const level = Number(ownKey && ownKey.split('.').length);
  const isLevel3OrMore = level >= 3;

  const hasChildrenWithErrors = useMemo(() => {
    return !isEmptyFn(value) && !!validationErrors;
  }, [value, validationErrors]);

  return (
    <>
      <Dialog
        as="div"
        open={isConfirmSectionModalOpen}
        className={clsx('fixed inset-0 overflow-y-auto', MODAL_Z_INDEX)}
        onClose={handleCancelSectionAction}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

          <DialogPanel
            transition
            className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="relative inline-block max-w-[600px] transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:p-6 sm:align-middle">
              <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden disabled:opacity-50"
                  onClick={handleCancelSectionAction}
                  disabled={isSectionActionLoading}
                >
                  <span className="sr-only">Cancel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="bg-warning-50 mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon className="text-warning-600 h-6 w-6" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" className="text-info-900 text-lg leading-6 font-medium">
                    {pendingAction === 'ADD' ? 'Add a new section?' : 'Remove this section?'}
                  </DialogTitle>
                  <div className="mt-3">
                    <p className="text-info-500 text-sm">
                      {pendingAction === 'ADD'
                        ? 'Before adding a new section, any unsaved changes must be saved.'
                        : 'Removing this section will also delete all related data from the contract.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8 items-center justify-end gap-x-2.5 sm:mt-8 sm:flex">
                <Button
                  className="h-9"
                  onClick={handleCancelSectionAction}
                  isDisabled={isSectionActionLoading}
                  kind="secondary"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  className="h-9"
                  onClick={handleConfirmSectionAction}
                  kind="primary"
                  size="sm"
                  loading={isSectionActionLoading}
                >
                  {pendingAction === 'ADD' ? 'Yes, save and add section' : 'Yes, remove section'}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      {collapsible ? (
        <SectionRepeaterCollapsibleView
          autoGrow={autoGrow}
          isRequired={isRequired || hasChildrenWithErrors}
          canAdd={canAdd}
          className={clsx(className, isLevel3OrMore && '!p-0')}
          emptyContent={emptyContent}
          handleAdd={handleAdd}
          handleRemove={handleRemove}
          helperText={helperText}
          id={id}
          initialItems={initialItems}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          item={item}
          onChange={onChange}
          ownKey={ownKey}
          schemaItem={schemaItem}
          showQuestionKey={showQuestionKey}
          title={item.title}
          description={item['ui:description']}
          total100Error={total100Error}
          validationErrors={validationErrors}
          startCollapsed={startCollapsed}
          hideErrors={hideErrors}
        />
      ) : (
        <SectionRepeaterView
          autoGrow={autoGrow}
          isRequired={isRequired || hasChildrenWithErrors}
          hideErrors={hideErrors}
          canAdd={canAdd}
          className={clsx(className, isLevel3OrMore && '!p-0')}
          emptyContent={emptyContent}
          handleAdd={handleAdd}
          handleRemove={handleRemove}
          helperText={helperText}
          id={id}
          initialItems={initialItems}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          item={item}
          onChange={onChange}
          ownKey={ownKey}
          schemaItem={schemaItem}
          showQuestionKey={showQuestionKey}
          title={item.title}
          total100Error={total100Error}
          validationErrors={validationErrors}
        />
      )}
    </>
  );
};

export default SectionRepeater;
